import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight, Mail } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { LogoLockup, LogoMark } from "./Logo";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services" },
  { to: "/it-infrastructure-monitoring", label: "Monitoring" },
  { to: "/about", label: "About" },
  { to: "/blog", label: "Blog" },
  { to: "/contact", label: "Contact" },
];

const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => setOpen(false), [location.pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-[#050507]/80 border-b border-white/10"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 h-16 md:h-20 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="group">
          <LogoLockup variant="dark" />
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-link-${n.label.toLowerCase()}`}
              className={({ isActive }) =>
                `text-sm tracking-wide transition-colors relative py-1 ${
                  isActive ? "text-white" : "text-[#8B93A5] hover:text-white"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {n.label}
                  {isActive && (
                    <motion.span
                      layoutId="nav-underline"
                      className="absolute -bottom-1 left-0 right-0 h-px bg-[#39FF14]"
                    />
                  )}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <Link
            to="/contact"
            data-testid="nav-cta-demo"
            className="btn-sweep bg-[#39FF14] text-black font-bold uppercase tracking-wider px-5 py-3 text-xs hover:bg-[#32E612] transition-colors flex items-center gap-2"
          >
            Request Demo
            <ArrowUpRight className="w-3.5 h-3.5" />
          </Link>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
          className="lg:hidden w-10 h-10 border border-white/10 flex items-center justify-center"
          onClick={() => setOpen((v) => !v)}
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="lg:hidden overflow-hidden bg-[#050507] border-t border-white/10"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {NAV.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  data-testid={`mobile-nav-link-${n.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `text-base ${isActive ? "text-white" : "text-[#8B93A5]"}`
                  }
                >
                  {n.label}
                </NavLink>
              ))}
              <Link
                to="/contact"
                data-testid="mobile-nav-cta"
                className="bg-[#39FF14] text-black font-bold uppercase tracking-wider px-5 py-3 text-xs text-center mt-2"
              >
                Request Demo
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

const Footer = () => (
  <footer data-testid="site-footer" className="border-t border-white/10 bg-[#050507] mt-24">
    <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-16 grid md:grid-cols-12 gap-10">
      <div className="md:col-span-5">
        <Link to="/" className="inline-block">
          <LogoLockup variant="dark" />
        </Link>
        <p className="mt-5 text-[#8B93A5] max-w-md leading-relaxed">
          SOC Level 2 cybersecurity for modern enterprises. Proactive threat detection,
          vulnerability management, and 24/7 monitoring — built by operators with 40+ years of
          combined industry experience.
        </p>
        <div className="mt-6 flex items-center gap-3 font-mono text-xs text-[#00F0FF]">
          <span className="w-2 h-2 bg-[#39FF14] pulse-dot" />
          SOC STATUS: OPERATIONAL
        </div>
      </div>

      <div className="md:col-span-3">
        <h4 className="font-mono text-xs tracking-[0.2em] uppercase text-[#00F0FF] mb-4">
          Navigate
        </h4>
        <ul className="space-y-3">
          {NAV.map((n) => (
            <li key={n.to}>
              <Link
                to={n.to}
                data-testid={`footer-link-${n.label.toLowerCase()}`}
                className="text-[#8B93A5] hover:text-white transition-colors"
              >
                {n.label}
              </Link>
            </li>
          ))}
        </ul>
      </div>

      <div className="md:col-span-4">
        <h4 className="font-mono text-xs tracking-[0.2em] uppercase text-[#00F0FF] mb-4">
          Contact
        </h4>
        <a
          href="mailto:info@quasect.com"
          data-testid="footer-email-link"
          className="flex items-center gap-2 text-white hover:text-[#39FF14] transition-colors"
        >
          <Mail className="w-4 h-4" /> info@quasect.com
        </a>
        <p className="mt-3 text-[#8B93A5] text-sm">
          Need an immediate threat response? Reach out to our SOC analysts directly.
        </p>
        <Link
          to="/contact"
          data-testid="footer-cta-secure"
          className="inline-flex items-center gap-2 mt-5 text-sm uppercase tracking-wider border border-white/20 hover:border-white px-4 py-2.5 transition-colors"
        >
          Secure Your Business <ArrowUpRight className="w-3.5 h-3.5" />
        </Link>
      </div>
    </div>
    <div className="border-t border-white/10">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-6 flex flex-col md:flex-row gap-3 justify-between items-start md:items-center text-xs text-[#8B93A5] font-mono">
        <span>© {new Date().getFullYear()} QUASECT — ALL RIGHTS RESERVED</span>
        <span>SOC L2 / VAPT / 24x7 MONITORING</span>
      </div>
    </div>
  </footer>
);

export default function Layout() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [pathname]);

  return (
    <div className="min-h-screen bg-[#050507] text-white">
      <Header />
      <main className="pt-16 md:pt-20">
        <Outlet />
      </main>
      <Footer />
    </div>
  );
}
